import React from 'react';
import { ClockIcon, EyeOffIcon, HourglassIcon, RepeatIcon, RotateCcwIcon, RouteIcon, SnailIcon, TriangleAlertIcon, UnlinkIcon, UsersIcon, XIcon } from 'lucide-react';
import { SectionHeading } from './SectionHeading';
import { arrivalLines, CostIcon, costs, finalCost, sessionLines } from '../data/copy';
const costIcons: Record<CostIcon, typeof ClockIcon> = {
  clock: ClockIcon,
  repeat: RepeatIcon,
  hourglass: HourglassIcon,
  route: RouteIcon,
  alert: TriangleAlertIcon,
  crowd: UsersIcon,
  unlink: UnlinkIcon,
  slow: SnailIcon,
  eye: EyeOffIcon
};
export function ProblemSection() {
  return <section className="defer-paint w-full bg-navy py-10 sm:py-14" aria-labelledby="problem-heading" data-id="element-162" data-mp="3jgbrkp9p18i0">
      <div className="mx-auto max-w-6xl px-6" data-id="element-163" data-mp="3egpfwk9ik0bb">
        <SectionHeading eyebrow="The real problem" eyebrowTone="danger" title={<span id="problem-heading" data-id="element-165" data-mp="1p2u22wpqa1kc">Does this sound familiar?</span>} data-id="element-164" data-mp="3eew5ctvs4vy1" />

        <div className="mx-auto mt-8 grid max-w-5xl gap-10 lg:grid-cols-2 lg:gap-16" data-id="element-166" data-mp="1k3hj4n1z9en1">
          <div data-id="element-167" data-mp="jddf4g0mmqz8">
            <p className="font-display text-base font-semibold uppercase tracking-[0.2em] text-muted" data-id="element-168" data-mp="jdwmj2snhwz0">
              Training starts at
            </p>
            <p className="font-display text-7xl font-extrabold leading-none text-accent sm:text-8xl" data-id="element-169" data-mp="oa3qiq1hkyeb">
              6:00 PM.
            </p>
            <ul className="mt-6 space-y-1.5 text-lg text-fg/90" data-id="element-170" data-mp="x4ry4mujas6a">
              {arrivalLines.map(line => <li key={line} data-id="element-171" data-mp="1244jf35rbr6r">{line}</li>)}
            </ul>
            <p className="mt-6 font-display text-2xl font-bold uppercase leading-tight text-fg sm:text-3xl" data-id="element-172" data-mp="125pfix7pzi6q">
              And you are still trying to decide what to coach.
            </p>
            <p className="mt-5 leading-relaxed text-muted" data-id="element-173" data-mp="1c2kbb8clz4hb">
              You spent part of the day searching YouTube, saving drills, taking
              screenshots, opening different websites, and trying to connect
              exercises from different sources.
            </p>
          </div>

          <div className="lg:border-l lg:border-line lg:pl-16" data-id="element-174" data-mp="3hv0vahosv4dj">
            <p className="font-display text-3xl font-bold uppercase leading-tight text-fg sm:text-4xl" data-id="element-175" data-mp="ozh6ukkx1iqd">
              Then training begins.
            </p>
            <ul className="mt-6 divide-y divide-line border-y border-line" data-id="element-176" data-mp="k3injbeg2qwt">
              {sessionLines.map(line => <li key={line} className="flex items-start gap-3 py-3.5 text-[1.02rem] text-fg/90" data-id="element-177" data-mp="xym4zpskfa20">
                  <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-danger/15" aria-hidden="true" data-id="element-178" data-mp="17z4ub02wfirb">
                    <XIcon className="h-3 w-3 text-danger" strokeWidth={3} data-id="element-179" data-mp="12z0c95mcefyz" />
                  </span>
                  {line}
                </li>)}
            </ul>
            <p className="mt-6 text-muted" data-id="element-180" data-mp="1cpuxc6nur9iu">And after spending hours preparing...</p>
            <p className="mt-1 font-display text-3xl font-extrabold uppercase leading-tight text-fg sm:text-4xl" data-id="element-181" data-mp="1crxwbg6zgczp">
              the session still feels <span className="text-danger" data-id="element-182" data-mp="1lr1g6mdz4wf9">improvised.</span>
            </p>
          </div>
        </div>

        <div className="mt-12 sm:mt-16" data-id="element-183" data-mp="25c1r33e65s5z">
          <h3 className="mx-auto max-w-3xl text-center font-display text-3xl font-extrabold uppercase leading-tight text-fg sm:text-4xl" data-id="element-184" data-mp="25d4irdjbfymy">
            The real cost of not having the right system
          </h3>

          <ul className="mx-auto mt-8 grid max-w-4xl grid-cols-2 gap-2.5 sm:mt-10 sm:gap-4" data-id="element-185" data-mp="1wdzhw0ka7t7m">
            {costs.map((cost, index) => {
            const Icon = costIcons[cost.icon];
            const isLastOdd = index === costs.length - 1 && costs.length % 2 === 1;
            return <li key={cost.title} className={`flex flex-col gap-2.5 rounded-2xl border p-3.5 sm:flex-row sm:items-center sm:gap-4 sm:rounded-3xl sm:p-5 ${isLastOdd ? 'col-span-2 sm:mx-auto sm:w-[calc(50%-0.5rem)]' : ''}`} style={{
              backgroundColor: `${cost.color}0d`,
              borderColor: `${cost.color}33`
            }} data-id="element-186" data-mp="1re4qlvwlh1m1" data-mpi={index}>
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl sm:h-12 sm:w-12 sm:rounded-2xl" style={{
                backgroundColor: cost.color
              }} aria-hidden="true" data-id="element-187" data-mp="18lssjlcz4mhp">
                    <Icon className="h-4 w-4 text-white sm:h-6 sm:w-6" strokeWidth={2} data-id="element-188" data-mp="vm00zkp7dcao" />
                  </span>
                  <div className="min-w-0" data-id="element-189" data-mp="qui2r4ekdpte">
                    <p className="font-display text-base font-bold uppercase leading-[1.05] text-fg sm:text-xl" data-id="element-190" data-mp="qt6x0z1k67zn">
                      {cost.title}
                    </p>
                    <p className="mt-1 text-[0.8rem] leading-snug text-muted sm:text-[0.9rem]" data-id="element-191" data-mp="hwg4gwm7tb9t">
                      {cost.detail.join(' ')}
                    </p>
                  </div>
                </li>;
          })}
          </ul>

          <div className="mx-auto mt-5 flex max-w-4xl flex-col items-center gap-3 rounded-3xl border-2 border-accent/40 bg-accent/[0.06] px-6 py-9 text-center sm:px-10 sm:py-12" data-id="element-192" data-mp="3v3pooisjfpi">
            <span className="flex h-14 w-14 items-center justify-center rounded-full bg-accent/15" aria-hidden="true" data-id="element-193" data-mp="228m4iwre8i90">
              <RotateCcwIcon className="h-7 w-7 text-accent" strokeWidth={2} data-id="element-194" data-mp="3g6rhle4v6qog" />
            </span>
            <p className="mt-2 font-display text-2xl font-extrabold uppercase leading-tight text-fg sm:text-3xl" data-id="element-195" data-mp="3l5uqj9nyth1p">
              {finalCost.title}
            </p>
            <p className="text-muted" data-id="element-196" data-mp="3px2ye19ncucr">{finalCost.lead}</p>
            <p className="mt-1 font-display text-4xl font-extrabold leading-[1.05] text-accent sm:text-5xl" data-id="element-197" data-mp="3tyo6787bqig3">
              {finalCost.question}
            </p>
          </div>
        </div>
      </div>
    </section>;
}